import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, ShieldCheck, ArrowRight } from "lucide-react";
import {
  getArchitectUnreadCount,
  dismissArchitectDmBanner,
} from "../lib/architectDm";
import { useAuth } from "../contexts/AuthContext";

const SEEN_KEY = "sentinel_architect_dm_seen_ids";

function loadSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveSeen(set) {
  try {
    // cap to last 200 ids
    const arr = Array.from(set).slice(-200);
    localStorage.setItem(SEEN_KEY, JSON.stringify(arr));
  } catch {
    /* ignore */
  }
}

/** Persistent-poll banner for the most recent unread Architect DM.
 *  Dismissing hides THIS message forever (banner_dismissed_at is
 *  written server-side). Newer messages resurface a fresh banner.
 *  Also fires a sonner toast the first time a new message id is
 *  observed on this browser (tracked in localStorage). */
export default function ArchitectBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [unread, setUnread] = useState(0);
  const seenRef = useRef(loadSeen());
  const firstTickRef = useRef(true);

  useEffect(() => {
    if (!user?.user_id || String(user.user_id).startsWith("anon_")) return;
    let cancelled = false;
    const tick = () => {
      getArchitectUnreadCount()
        .then((d) => {
          if (cancelled) return;
          const nextBanner = d?.banner || null;
          setBanner(nextBanner);
          setUnread(d?.unread || 0);
          // Fire a toast on genuinely NEW ids only; suppress on the
          // first tick after mount so a page refresh doesn't spam.
          if (nextBanner && !firstTickRef.current) {
            if (!seenRef.current.has(nextBanner.id)) {
              seenRef.current.add(nextBanner.id);
              saveSeen(seenRef.current);
              toast(
                nextBanner.title || "New message from the Architect",
                {
                  description:
                    (nextBanner.body || "").slice(0, 140) +
                    ((nextBanner.body || "").length > 140 ? "…" : ""),
                  duration: 8000,
                  action: {
                    label: "Open",
                    onClick: () => navigate("/inbox"),
                  },
                }
              );
            }
          } else if (nextBanner) {
            seenRef.current.add(nextBanner.id);
            saveSeen(seenRef.current);
          }
          firstTickRef.current = false;
        })
        .catch(() => {
          /* silent */
        });
    };
    tick();
    const iv = setInterval(tick, 45_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  if (!banner) return null;

  const onDismiss = async (e) => {
    e.stopPropagation();
    const id = banner.id;
    setBanner(null);
    try {
      await dismissArchitectDmBanner(id);
    } catch {
      /* silent — will resurface on next poll if it fails */
    }
  };

  const onOpen = () => {
    navigate("/inbox");
  };

  return (
    <div
      data-testid="architect-banner"
      className="w-full border-b border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent"
    >
      <div className="flex items-center gap-3 px-4 sm:px-6 py-2 text-[11px] font-mono">
        <span className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 border border-amber-500/60 bg-amber-500/10 text-amber-300 uppercase tracking-[0.25em]">
          <ShieldCheck className="h-3 w-3" /> Architect
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="flex-1 min-w-0 text-left flex items-center gap-2 text-zinc-200 hover:text-amber-200 transition-colors"
          data-testid="architect-banner-open"
        >
          <span className="font-medium truncate">{banner.title}</span>
          <span className="hidden sm:inline text-zinc-500 truncate">
            · {banner.body?.slice(0, 90)}
            {banner.body && banner.body.length > 90 ? "…" : ""}
          </span>
          {unread > 1 && (
            <span className="ml-auto shrink-0 px-1.5 py-0.5 border border-amber-500/50 text-amber-300 text-[9px] uppercase tracking-[0.25em]">
              {unread - 1} more
            </span>
          )}
          <ArrowRight className="h-3 w-3 shrink-0 opacity-70" />
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 p-1 text-zinc-500 hover:text-amber-300 transition-colors"
          data-testid="architect-banner-dismiss"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
